import React from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Copy, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loading } from "@/components/common/Loading";
import { MODE_LABEL } from "@/components/groups/GroupCard";
import { LocationFields } from "@/components/forms/AddressFields";
import { ImageUploader, CoverUploader } from "@/components/forms/MediaUploader";
import { RichTextEditor, FULL_TOOLBAR } from "@/components/forms/RichTextEditor";
import { GroupQuestionsManager } from "@/components/groups/GroupQuestionsManager";
import { GroupModerationPanel } from "@/components/groups/GroupModerationPanel";
import { GroupMemberAnswers } from "@/components/groups/GroupMemberAnswers";
import { GroupAffiliations } from "@/components/groups/GroupAffiliations";
import { GroupDangerZone } from "@/components/groups/GroupDangerZone";
import { ConfirmMemberActionDialog } from "@/components/groups/ConfirmMemberActionDialog";
import { PersonLink } from "@/components/groups/PersonLink";
import { STICKY_TABBAR, useStickyNavOffset } from "@/components/groups/groupSticky";

// Mirrors the server caps (`services/group_models`), surfaced through /api/groups/config.
const NAME_MAX = 100;
// Mirrors `services/group_models.MAX_INDUSTRIES` — the server is still the authority.
const MAX_INDUSTRIES = 10;
const SHORT_DESC_MAX = 300;
// Rules / welcome message are prose with lists and links, not documents — the same
// constrained subset of the canonical toolbar the platform uses for shorter rich text.
const RULES_TOOLBAR = ["bold", "italic", "underline", "bullet", "ordered", "link"];
import { MultiSelect } from "@/components/directory/MultiSelect";

const TABS = ["overview", "members", "requests", "invitations", "questions",
  "affiliations", "moderation", "settings", "audit"];

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

function ReasonDialog({ open, onOpenChange, title, description, required, onConfirm, testId }) {
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Textarea value={reason} rows={3} maxLength={500} placeholder="Reason"
                  onChange={(e) => setReason(e.target.value)} data-testid={`${testId}-input`} />
        <DialogFooter>
          <Button disabled={busy || (required && !reason.trim())} data-testid={`${testId}-confirm`}
                  onClick={async () => { setBusy(true); await onConfirm(reason); setBusy(false); onOpenChange(false); }}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GroupManage() {
  const { groupId } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  useStickyNavOffset();
  const [params, setParams] = useSearchParams();
  // `?tab=requests` lets the pending-request indicators deep-link straight to the
  // approval queue (GRP-DEC-312); an unknown value falls back to Overview.
  const requested = params.get("tab");
  const [tab, setTab] = React.useState(TABS.includes(requested) ? requested : "overview");
  const [dialog, setDialog] = React.useState(null);      // {kind, userId}
  const [linkUrl, setLinkUrl] = React.useState(null);
  const [inviteEmail, setInviteEmail] = React.useState("");
  const [openAnswers, setOpenAnswers] = React.useState(null);
  const [form, setForm] = React.useState(null);

  const group = useQuery({
    queryKey: ["group-manage", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}`)).data,
    retry: false,
  });
  const g = group.data?.group;
  const viewer = group.data?.viewer || {};

  React.useEffect(() => { if (g && !form) setForm({ ...g }); }, [g]);          // eslint-disable-line

  const members = useQuery({
    queryKey: ["group-manage-members", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/members`, { params: { limit: 200 } })).data,
    enabled: Boolean(g) && ["overview", "members", "settings"].includes(tab),
  });
  const requests = useQuery({
    queryKey: ["group-manage-requests", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/requests`)).data,
    // Always loaded: the Requests tab carries the pending-count badge (GRP-DEC-312), so
    // the count must be known from whichever tab the organizer is standing on.
    enabled: Boolean(g),
  });
  const invitations = useQuery({
    queryKey: ["group-manage-invitations", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/invitations`)).data,
    enabled: Boolean(g) && ["overview", "invitations"].includes(tab),
  });
  const links = useQuery({
    queryKey: ["group-manage-links", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/invite-links`)).data,
    enabled: Boolean(g) && tab === "invitations",
  });
  const banned = useQuery({
    queryKey: ["group-manage-banned", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/banned`)).data,
    enabled: Boolean(g) && tab === "members",
  });
  const questions = useQuery({
    queryKey: ["group-manage-questions", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/questions`)).data,
    enabled: Boolean(g) && tab === "questions",
  });
  const audit = useQuery({
    queryKey: ["group-manage-audit", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/audit`)).data,
    enabled: Boolean(g) && tab === "audit",
  });
  // New-member indicator (GRP-DEC-313). A LIVE count of this organizer's unread
  // new-member notifications — never a lifetime counter — and semantically separate from
  // the pending-request badge above.
  const newMembers = useQuery({
    queryKey: ["groups-new-member-count"],
    queryFn: async () => (await api.get("/groups/new-members/count")).data,
    enabled: Boolean(g),
    retry: false,
  });
  const newMemberCount = newMembers.data?.by_group?.[groupId] || 0;

  // Phase 7 — the pending Group-moderation badge. Always loaded (like the pending-request
  // count) so the badge is right from whichever tab the organizer is standing on, and it is
  // a LIVE server count of actionable items, not a stored counter. No polling.
  const modCounts = useQuery({
    queryKey: ["group-moderation-counts", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/moderation/counts`)).data,
    enabled: Boolean(g),
    retry: false,
  });
  const pendingReports = modCounts.data?.pending || 0;
  // A moderation notification deep-links to one report; the panel opens it directly.
  const requestedReport = params.get("report");

  // Opening member management IS the review, so it acknowledges this organizer's
  // new-member activity for this Group (and only theirs — the server pins the filter to
  // the caller).
  React.useEffect(() => {
    if (tab !== "members" || !g || !newMemberCount) return;
    api.post(`/groups/${groupId}/new-members/read`).then(() => {
      qc.invalidateQueries({ queryKey: ["groups-new-member-count"] });
      qc.invalidateQueries({ queryKey: ["groups-mine"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, g, newMemberCount, groupId]);
  const refs = useQuery({
    queryKey: ["groups-refs"],
    queryFn: async () => {
      const [cats, inds, tops, classes] = await Promise.all([
        api.get("/reference/group_categories"), api.get("/reference/industries"),
        api.get("/reference/skills_interests"),
        api.get("/reference/business_classifications"),
      ]);
      return { categories: cats.data, industries: inds.data, topics: tops.data,
        classifications: classes.data };
    },
    staleTime: 10 * 60 * 1000,
  });

  const refresh = () => TABS.forEach((t) =>
    qc.invalidateQueries({ queryKey: [`group-manage-${t === "overview" ? "members" : t}`, groupId] }))
    || qc.invalidateQueries({ queryKey: ["group-manage", groupId] });

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); refresh();
      qc.invalidateQueries({ queryKey: ["group-manage", groupId] });
      qc.invalidateQueries({ queryKey: ["group-manage-members", groupId] });
      qc.invalidateQueries({ queryKey: ["group-manage-requests", groupId] });
      qc.invalidateQueries({ queryKey: ["group-manage-invitations", groupId] });
      qc.invalidateQueries({ queryKey: ["group-manage-links", groupId] });
      qc.invalidateQueries({ queryKey: ["group-manage-banned", groupId] });
      // Deciding a request changes the actionable count behind the nav badge and the
      // per-Group indicator on /dashboard/groups (GRP-DEC-312).
      qc.invalidateQueries({ queryKey: ["groups-pending-request-count"] });
      qc.invalidateQueries({ queryKey: ["groups-mine"] });
    } catch (err) { toast.error(err?.response?.data?.detail || "That didn't work."); }
  };

  if (group.isLoading) return <Loading />;
  if (group.isError || !g) {
    return <Card className="border-0 p-10 text-center" data-testid="group-manage-not-found">
      <p className="font-semibold">Group not found</p>
    </Card>;
  }
  if (!viewer.is_admin) {
    return <Card className="border-0 p-10 text-center" data-testid="group-manage-forbidden">
      <p className="font-semibold">Only organizers can manage this group</p>
      <Button asChild variant="outline" className="mt-4">
        <Link to="/dashboard/groups">Back to My Groups</Link>
      </Button>
    </Card>;
  }

  const save = () => act(async () => {
    const payload = {
      display_name: form.display_name, short_description: form.short_description,
      about: form.about, rules: form.rules, welcome_message: form.welcome_message,
      category: form.category, industries: form.industries, topics: form.topics,
      business_classifications: form.business_classifications,
      membership_mode: form.membership_mode, listed: form.listed,
      invite_permission: form.invite_permission, show_organizers: form.show_organizers,
      allow_member_messages: form.allow_member_messages,
      location: form.location, is_online: form.is_online, is_local: form.is_local,
      profile_image_url: form.profile_image_url, cover_url: form.cover_url,
      ...(viewer.is_owner && g.state === "draft" ? { desired_handle: form.desired_handle } : {}),
      ...(viewer.is_owner && g.state === "active" && form.handle !== g.handle
        ? { handle: form.handle } : {}),
    };
    await api.put(`/groups/${groupId}`, payload);
  }, "Settings saved.");

  return (
    <div className="space-y-6" data-testid="group-manage-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/dashboard/groups" data-testid="manage-back-to-my-groups"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />Back to My Groups
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">{g.display_name}</h1>
          {g.url ? (
            <Link to={g.url} data-testid="manage-view-group"
                  className="text-sm text-muted-foreground hover:text-foreground">
              View group page
            </Link>
          ) : null}
        </div>
        {g.state === "draft" ? (
          <Button data-testid="group-publish-button" onClick={() => act(async () => {
            await api.post(`/groups/${groupId}/publish`, { handle: form?.desired_handle || null });
          }, "Group published.")}>Publish group</Button>
        ) : <Badge variant="secondary">Published</Badge>}
      </div>

      <Tabs value={tab} className={STICKY_TABBAR} onValueChange={(t) => {
        setTab(t);
        if (params.get("tab") || params.get("report")) {
          params.delete("tab");
          params.delete("report");
          setParams(params, { replace: true });
        }
      }}>
        {/* Manage's secondary navigation follows the same sticky vocabulary as the Group
            tab bar, so a long settings panel never scrolls it away (correction §3). */}
        <div>
        <TabsList className="flex-wrap" data-testid="group-manage-tabs">
          {TABS.map((t) => (
            <TabsTrigger key={t} value={t} className="capitalize" data-testid={`group-manage-tab-${t}`}>
              {t}
              {t === "requests" && (requests.data?.total || 0) > 0 ? (
                <span className="ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-[640] text-primary-foreground"
                      data-testid="group-manage-requests-badge">
                  {requests.data.total > 9 ? "9+" : requests.data.total}
                </span>
              ) : null}
              {t === "members" && newMemberCount > 0 ? (
                <span className="ml-1.5 inline-flex h-5 items-center justify-center rounded-full bg-teal-100 px-1.5 text-[11px] font-[640] text-teal-800"
                      data-testid="group-manage-new-members-badge"
                      aria-label={`${newMemberCount} new member${newMemberCount === 1 ? "" : "s"} to review`}>
                  {newMemberCount > 9 ? "9+" : newMemberCount} new
                </span>
              ) : null}
              {t === "moderation" && pendingReports > 0 ? (
                <span className="ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-100 px-1.5 text-[11px] font-[640] text-amber-900"
                      data-testid="group-manage-moderation-badge"
                      aria-label={`${pendingReports} report${pendingReports === 1 ? "" : "s"} pending review`}>
                  {pendingReports > 9 ? "9+" : pendingReports}
                </span>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>
        </div>
      </Tabs>

      {tab === "overview" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="group-overview-panel">
          {[["Members", g.member_count], ["Pending requests", requests.data?.total ?? 0],
            ["Invitations", invitations.data?.total ?? 0],
            ["Discovery", g.listed ? "Listed" : "Unlisted"]].map(([label, value]) => (
            <Card key={label} className="p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
              <p className="mt-1 text-2xl font-semibold">{value}</p>
            </Card>
          ))}
        </div>
      ) : null}

      {tab === "members" ? (
        <div className="space-y-4" data-testid="group-manage-members-panel">
          {(members.data?.members || []).map((mem) => (
            <Card key={mem.user_id} className="flex flex-wrap items-center justify-between gap-3 p-3"
                  data-testid={`manage-member-${mem.user_id}`}>
              <PersonLink person={mem} testId={`manage-member-link-${mem.user_id}`}
                          subtitle={`${mem.role} · joined ${fmt(mem.joined_at)}`} />
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="ghost" data-testid={`manage-answers-toggle-${mem.user_id}`}
                        onClick={() => setOpenAnswers(
                          openAnswers === mem.user_id ? null : mem.user_id)}>
                  {openAnswers === mem.user_id ? "Hide answers" : "View answers"}
                </Button>
                {viewer.is_owner && mem.role !== "owner" ? (
                  <Button size="sm" variant="outline" data-testid={`manage-role-${mem.user_id}`}
                          onClick={() => setDialog({
                            kind: mem.role === "admin" ? "demote" : "promote",
                            userId: mem.user_id, name: mem.display_name,
                            nextRole: mem.role === "admin" ? "member" : "admin",
                          })}>
                    {mem.role === "admin" ? "Demote" : "Make admin"}
                  </Button>
                ) : null}
                {viewer.is_owner && mem.role === "admin" ? (
                  <Button size="sm" variant="outline" data-testid={`manage-transfer-${mem.user_id}`}
                          onClick={() => setDialog({
                            kind: "transfer", userId: mem.user_id,
                            name: mem.display_name,
                          })}>
                    Transfer ownership
                  </Button>
                ) : null}
                {mem.role !== "owner" ? (
                  <>
                    <Button size="sm" variant="ghost" data-testid={`manage-remove-${mem.user_id}`}
                            onClick={() => setDialog({ kind: "remove", userId: mem.user_id })}>
                      Remove
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive"
                            data-testid={`manage-ban-${mem.user_id}`}
                            onClick={() => setDialog({ kind: "ban", userId: mem.user_id })}>
                      Ban
                    </Button>
                  </>
                ) : null}
              </div>
              {openAnswers === mem.user_id ? (
                <GroupMemberAnswers groupId={groupId} userId={mem.user_id} />
              ) : null}
            </Card>
          ))}
          {(banned.data?.banned || []).length ? (
            <div className="space-y-2 pt-2" data-testid="group-banned-list">
              <h2 className="text-base font-semibold">Banned</h2>
              {banned.data.banned.map((b) => (
                <Card key={b.user_id} className="flex items-center justify-between gap-3 p-3">
                  <div>
                    <PersonLink person={b} testId={`manage-banned-link-${b.user_id}`}
                                subtitle={b.ban?.reason} />
                  </div>
                  <Button size="sm" variant="outline" data-testid={`manage-unban-${b.user_id}`}
                          onClick={() => act(async () => api.post(
                            `/groups/${groupId}/members/${b.user_id}/unban`), "Member unbanned.")}>
                    Unban
                  </Button>
                </Card>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === "requests" ? (
        <div className="space-y-3" data-testid="group-requests-panel">
          {(requests.data?.requests || []).map((r) => (
            <Card key={r.id} className="space-y-3 p-4" data-testid={`manage-request-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <PersonLink person={r.member} testId={`manage-request-link-${r.user_id}`}
                            subtitle={`Requested ${fmt(r.created_at)} · declined before: ${r.declined_count}`} />
                <div className="flex items-center gap-2">
                  <Button size="sm" data-testid={`manage-approve-${r.id}`}
                          onClick={() => act(async () => api.post(
                            `/groups/${groupId}/requests/${r.id}/approve`), "Member approved.")}>
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`manage-decline-${r.id}`}
                          onClick={() => setDialog({ kind: "decline", requestId: r.id })}>
                    Decline
                  </Button>
                  {r.declined_count >= 8 ? (
                    <Button size="sm" variant="ghost" data-testid={`manage-reset-${r.user_id}`}
                            onClick={() => act(async () => api.post(
                              `/groups/${groupId}/requests/${r.user_id}/reset-block`),
                            "Request block reset.")}>
                      Reset block
                    </Button>
                  ) : null}
                </div>
              </div>
              {(r.answers || []).length ? (
                <div className="space-y-1 rounded-lg border bg-muted/30 p-3"
                     data-testid={`manage-request-answers-${r.id}`}>
                  {r.answers.map((a) => (
                    <p key={a.question_id} className="text-sm">
                      <span className="font-medium">{a.label}: </span>
                      <span className="text-muted-foreground">
                        {Array.isArray(a.value) ? a.value.join(", ") : String(a.value ?? "—")}
                      </span>
                    </p>
                  ))}
                </div>
              ) : null}
            </Card>
          ))}
          {!(requests.data?.requests || []).length ? (
            <p className="text-sm text-muted-foreground">No pending requests.</p>
          ) : null}
        </div>
      ) : null}

      {tab === "invitations" ? (
        <div className="space-y-5" data-testid="group-invitations-panel">
          <Card className="border-0 space-y-3 p-4">
            <h2 className="text-base font-semibold">Invite someone</h2>
            <div className="flex flex-wrap gap-2">
              <Input className="max-w-sm" value={inviteEmail} placeholder="name@example.com"
                     onChange={(e) => setInviteEmail(e.target.value)}
                     data-testid="group-invite-email" />
              <Button data-testid="group-invite-send" onClick={() => act(async () => {
                await api.post(`/groups/${groupId}/invitations`, { email: inviteEmail });
                setInviteEmail("");
              }, "Invitation on its way.")}>Send invite</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Works for existing members and for people who don't have an account yet
              (their invitation expires after 90 days).
            </p>
          </Card>

          <Card className="border-0 space-y-3 p-4">
            <h2 className="text-base font-semibold">Reusable links</h2>
            <div className="flex flex-wrap gap-2">
              {["invite", "request"].map((kind) => (
                <Button key={kind} variant="outline" size="sm"
                        data-testid={`group-create-${kind}-link`}
                        onClick={() => act(async () => {
                          const { data } = await api.post(`/groups/${groupId}/invite-links`,
                            { kind, max_uses: null, expires_in_hours: null });
                          setLinkUrl(data.url);
                        }, "Link created — copy it now.")}>
                  Create {kind} link
                </Button>
              ))}
            </div>
            {linkUrl ? (
              <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-2"
                   data-testid="group-new-link">
                <code className="flex-1 truncate text-xs">{linkUrl}</code>
                <Button size="sm" variant="ghost" onClick={() => {
                  navigator.clipboard?.writeText(linkUrl); toast.success("Copied.");
                }} data-testid="group-copy-link"><Copy className="h-4 w-4" /></Button>
              </div>
            ) : null}
            <p className="text-xs text-muted-foreground">
              A link is shown once and stored hashed — recreate it if you lose it. Request
              links always go through approval; invite links grant membership.
            </p>
            {(links.data?.links || []).map((l) => (
              <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 border-t pt-2"
                   data-testid={`group-link-${l.id}`}>
                <div className="text-sm">
                  <span className="font-medium capitalize">{l.kind} link</span>
                  <span className="text-muted-foreground"> ···{l.token_preview} · {l.uses}
                    {l.max_uses ? `/${l.max_uses}` : ""} uses · {l.state}</span>
                </div>
                {l.state === "active" ? (
                  <Button size="sm" variant="ghost" data-testid={`group-disable-link-${l.id}`}
                          onClick={() => act(async () => api.post(
                            `/groups/${groupId}/invite-links/${l.id}/disable`), "Link disabled.")}>
                    Disable
                  </Button>
                ) : null}
              </div>
            ))}
          </Card>

          <Card className="border-0 space-y-2 p-4">
            <h2 className="text-base font-semibold">Pending invitations</h2>
            {(invitations.data?.invitations || []).map((inv) => (
              <div key={inv.id} className="flex flex-wrap items-center justify-between gap-2 border-t pt-2"
                   data-testid={`group-invitation-${inv.id}`}>
                <div className="text-sm">
                  <p className="font-medium">{inv.invited_name || inv.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {inv.kind} · {inv.state}{inv.expires_at ? ` · expires ${fmt(inv.expires_at)}` : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`group-resend-${inv.id}`}
                          onClick={() => act(async () => api.post(
                            `/groups/${groupId}/invitations/${inv.id}/resend`), "Invitation resent.")}>
                    Resend
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`group-revoke-${inv.id}`}
                          onClick={() => act(async () => api.post(
                            `/groups/${groupId}/invitations/${inv.id}/revoke`), "Invitation revoked.")}>
                    Revoke
                  </Button>
                </div>
              </div>
            ))}
            {!(invitations.data?.invitations || []).length ? (
              <p className="text-sm text-muted-foreground">No pending invitations.</p>
            ) : null}
          </Card>
        </div>
      ) : null}

      {tab === "questions" ? (
        <GroupQuestionsManager groupId={groupId} initial={questions.data} onSaved={() => {
          refresh();
          qc.invalidateQueries({ queryKey: ["group-manage-questions", groupId] });
        }} />
      ) : null}

      {tab === "affiliations" ? (
        <GroupAffiliations groupId={groupId} />
      ) : null}

      {tab === "settings" && form ? (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="group-settings-panel">
          <Card className="border-0 space-y-4 p-5">
            <div className="space-y-1.5">
              <Label>Group name ({(form.display_name || "").length}/{NAME_MAX})</Label>
              <Input value={form.display_name || ""} maxLength={NAME_MAX}
                     onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                     data-testid="settings-name" />
            </div>
            <div className="space-y-1.5">
              <Label>{g.state === "draft" ? "Desired handle" : "Handle"}</Label>
              <Input disabled={!viewer.is_owner}
                     value={(g.state === "draft" ? form.desired_handle : form.handle) || ""}
                     onChange={(e) => setForm({
                       ...form,
                       [g.state === "draft" ? "desired_handle" : "handle"]:
                         e.target.value.replace(/[^A-Za-z0-9-]/g, ""),
                     })}
                     data-testid="settings-handle" />
              <p className="text-xs text-muted-foreground">
                {viewer.is_owner ? "Only the owner can change the handle."
                  : "Only the owner can change the handle."}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>
                Short description ({(form.short_description || "").length}/{SHORT_DESC_MAX})
              </Label>
              <Textarea rows={2} maxLength={SHORT_DESC_MAX}
                        value={form.short_description || ""}
                        onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                        data-testid="settings-short-description" />
            </div>
            {/* Identity image + banner. Same shared upload controls, same 4:1 banner crop
                as a Business / Capital profile. */}
            <div className="grid gap-4 sm:grid-cols-2">
              <ImageUploader value={form.profile_image_url}
                             onChange={(u) => setForm({ ...form, profile_image_url: u })}
                             label="Thumbnail / profile image (square)"
                             testId="settings-thumbnail" />
              <div>
                <Label className="mb-1.5 block">Banner</Label>
                <CoverUploader value={form.cover_url}
                               onChange={(u) => setForm({ ...form, cover_url: u })}
                               aspect={4} testId="settings-banner" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>About</Label>
              {/* The canonical Startup Otter rich-text editor — the same component,
                  toolbar, content model, sanitization and rendering a Business /
                  Capital About uses. */}
              <RichTextEditor value={form.about || ""}
                              onChange={(v) => setForm({ ...form, about: v })}
                              toolbar={FULL_TOOLBAR}
                              placeholder="About this group…"
                              testId="settings-about" />
            </div>
            <div className="space-y-1.5">
              <Label>Group rules</Label>
              {/* Same canonical rich-text system as About (GRP-DEC-300). Rules
                  ACCEPTANCE logic is unchanged — a Group with rules still requires an
                  explicit agreement to join. */}
              <RichTextEditor value={form.rules || ""}
                              onChange={(v) => setForm({ ...form, rules: v })}
                              toolbar={RULES_TOOLBAR}
                              placeholder="How members are expected to behave…"
                              testId="settings-rules" />
            </div>
            <div className="space-y-1.5">
              <Label>Welcome message</Label>
              <RichTextEditor value={form.welcome_message || ""}
                              onChange={(v) => setForm({ ...form, welcome_message: v })}
                              toolbar={RULES_TOOLBAR}
                              placeholder="What a new member sees when they join…"
                              testId="settings-welcome" />
            </div>
          </Card>
          <Card className="border-0 space-y-4 p-5">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="settings-category"><SelectValue placeholder="Choose one" /></SelectTrigger>
                <SelectContent>
                  {(refs.data?.categories || []).map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Membership mode</Label>
              <Select value={form.membership_mode}
                      onValueChange={(v) => setForm({ ...form, membership_mode: v })}>
                <SelectTrigger data-testid="settings-membership-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MODE_LABEL).map(([v, label]) => (
                    <SelectItem key={v} value={v}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Who can invite</Label>
              <Select value={form.invite_permission}
                      onValueChange={(v) => setForm({ ...form, invite_permission: v })}>
                <SelectTrigger data-testid="settings-invite-permission"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admins">Owner and admins only</SelectItem>
                  <SelectItem value="members">All members</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Industry</Label>
              {/* GRP-DEC-327: the SAME canonical `industries` reference list every other
                  profile uses, through the SAME shared MultiSelect as Classification and
                  the directory filters. No Groups-only taxonomy and no second control. */}
              <MultiSelect label="Any industry"
                           options={refs.data?.industries || []}
                           selected={form.industries || []}
                           onChange={(arr) => setForm({ ...form, industries: arr })}
                           testId="settings-industries" />
              <p className="text-xs text-muted-foreground">
                Optional. Pick up to {MAX_INDUSTRIES} — they help people find this group in
                the directory.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Topics &amp; interests</Label>
              <MultiSelect label="Any topic"
                           options={refs.data?.topics || []}
                           selected={form.topics || []}
                           onChange={(arr) => setForm({ ...form, topics: arr })}
                           testId="settings-topics" />
              <p className="text-xs text-muted-foreground">
                The same skills &amp; interests list the rest of the platform uses.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Classification</Label>
              <MultiSelect label="Any classification"
                           options={refs.data?.classifications || []}
                           selected={form.business_classifications || []}
                           onChange={(arr) => setForm({ ...form, business_classifications: arr })}
                           testId="settings-classifications" />
              <p className="text-xs text-muted-foreground">
                The same classification list Business profiles use.
              </p>
            </div>
            {/* Canonical platform location fieldset — identical to Personal / Business /
                Capital / Event / Resource editors, so the Group is geocoded and filtered the
                same way. Location is OPTIONAL and independent of "Online group". */}
            <div className="space-y-1.5">
              <Label>Location</Label>
              <LocationFields value={form.location || {}} testIdPrefix="settings-location"
                              onChange={(l) => setForm({ ...form, location: l })} />
              <p className="text-xs text-muted-foreground">
                Optional. An online group can still have a home base — the two settings are
                independent.
              </p>
            </div>
            {[["listed", "Listed in discovery", "Unlisted groups stay out of the directory."],
              ["is_online", "Online group", "This group meets or connects online. It may still have a location."],
              ["is_local", "Local group", "This group is centred on its local area."],
              ["show_organizers", "Show organizer names", "Organizer names appear on the group page."],
              ["allow_member_messages", "Allow member messages",
                "Members can privately message each other inside this group. Turning this "
                + "off makes existing conversations read-only — nothing is deleted."]].map(
              ([key, label, hint]) => (
                <div key={key} className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs text-muted-foreground">{hint}</p>
                  </div>
                  <Switch checked={Boolean(form[key])} data-testid={`settings-${key}`}
                          onCheckedChange={(v) => setForm({ ...form, [key]: v })} />
                </div>
              ))}
            <Button onClick={save} data-testid="settings-save">Save settings</Button>
          </Card>
        </div>
      ) : null}

      {/* Phase 10 (§28) — Owner-only lifecycle controls. Archive is the recommended,
          reversible action; deletion is consequence-first with a typed confirmation. */}
      {tab === "settings" && form ? (
        <GroupDangerZone group={g} viewer={viewer} onChanged={() => {
          qc.invalidateQueries({ queryKey: ["groups-mine"] });
          navigate("/dashboard/groups");
        }} />
      ) : null}

      {tab === "moderation" ? (
        <GroupModerationPanel groupId={groupId} initialReportId={requestedReport} />
      ) : null}

      {tab === "audit" ? (
        <Card className="border-0 divide-y p-0" data-testid="group-audit-panel">
          {(audit.data?.entries || []).map((e) => (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
              <span className="font-medium">{e.action}</span>
              <span className="text-xs text-muted-foreground">{fmt(e.created_at)}</span>
            </div>
          ))}
          {!(audit.data?.entries || []).length ? (
            <p className="p-4 text-sm text-muted-foreground">No audit entries yet.</p>
          ) : null}
        </Card>
      ) : null}

      <ConfirmMemberActionDialog action={dialog?.kind === "promote" || dialog?.kind === "demote"
                                   || dialog?.kind === "transfer" ? dialog : null}
                                 onOpenChange={(v) => { if (!v) setDialog(null); }}
                                 onConfirm={async (done) => {
                                   const d = dialog;
                                   await act(async () => (d.kind === "transfer"
                                     ? api.post(`/groups/${groupId}/transfer-ownership`,
                                                { user_id: d.userId })
                                     : api.post(`/groups/${groupId}/members/${d.userId}/role`,
                                                { role: d.nextRole })), done);
                                   setDialog(null);
                                 }} />
      <ReasonDialog open={dialog?.kind === "remove"} onOpenChange={() => setDialog(null)}
                    title="Remove member" description="They can request or be invited again."
                    testId="remove-member-dialog"
                    onConfirm={(reason) => act(async () => api.post(
                      `/groups/${groupId}/members/${dialog.userId}/remove`, { reason }),
                    "Member removed.")} />
      <ReasonDialog open={dialog?.kind === "ban"} onOpenChange={() => setDialog(null)} required
                    title="Ban member"
                    description="A reason is required. Access ends on their next request."
                    testId="ban-member-dialog"
                    onConfirm={(reason) => act(async () => api.post(
                      `/groups/${groupId}/members/${dialog.userId}/ban`, { reason }),
                    "Member banned.")} />
      <ReasonDialog open={dialog?.kind === "decline"} onOpenChange={() => setDialog(null)}
                    title="Decline request" description="The reason is optional."
                    testId="decline-request-dialog"
                    onConfirm={(reason) => act(async () => api.post(
                      `/groups/${groupId}/requests/${dialog.requestId}/decline`, { reason }),
                    "Request declined.")} />
    </div>
  );
}
